import { useMemo, useState } from "react";
import Button from "app/storybookComponents/Button";
import { getLatestReflection } from "./helpers";
import { GoalItem } from "./types";
import ReflectionProgressBar from "./Reflection/ReflectionProgressBar";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "app/shadcnComponents/ui/drawer";
import { Separator } from "app/shadcnComponents/ui/separator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowTrendUp, faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  faBullseye,
  faCircleCheck,
  faTriangleExclamation,
} from "@fortawesome/pro-light-svg-icons";
import { DrawerSearchInput } from "./Modals/components/DrawerSearchInput";
import ReflectionStatusBadge from "./Reflection/ReflectionStatusBadge";
import AvatarCircle from "../AvatarCircle";
import { getS } from "utils/helperFunctions";
import { useNavigate } from "react-router-dom";
import { GOAL_DRAWER_WIDTH } from "./constants";

/* ---------------- Types ---------------- */

interface LinkedGoalsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  parentGoal?: GoalItem | null;
  closeDrawerOnChildClick?: boolean;
}

/* ---------------- Component ---------------- */

export default function LinkedGoalsDrawer({
  isOpen,
  onClose,
  parentGoal,
  closeDrawerOnChildClick = false,
}: Readonly<LinkedGoalsDrawerProps>) {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  /* -------- Enrich goals from reflections -------- */

  const enrichedGoals = useMemo(() => {
    return parentGoal?.child_goals?.map((goal) => {
      const reflection = getLatestReflection(goal);

      const progress =
        typeof reflection?.progressPercent === "number"
          ? reflection.progressPercent
          : null;

      const status =
        reflection?.status === "on_track" ||
        reflection?.status === "some_challenges" ||
        reflection?.status === "unlikely_to_reach"
          ? reflection.status
          : null;

      return {
        ...goal,
        progress,
        status,
      };
    });
  }, [parentGoal]);

  /* -------- Search -------- */

  const filteredGoals =
    enrichedGoals?.filter(
      (g) =>
        g.title.toLowerCase().includes(search.toLowerCase()) ||
        `${g.user?.firstName} ${g.user?.lastName}`
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        g.user?.jobTitle?.toLowerCase().includes(search.toLowerCase())
    ) ?? [];

  /* -------- Summary -------- */

  const summary = useMemo(() => {
    const total = enrichedGoals?.length ?? 0;
    const withProgress =
      enrichedGoals?.filter((g) => g.progress !== null) ?? [];

    const onTrack = enrichedGoals?.filter(
      (g) => g.status === "on_track"
    ).length;

    const someChanges = enrichedGoals?.filter(
      (g) => g.status === "some_challenges"
    ).length;

    const unlikely = enrichedGoals?.filter(
      (g) => g.status === "unlikely_to_reach"
    ).length;

    const avgProgress =
      withProgress?.length ?? 0
        ? Math.round(
            withProgress.reduce((sum, g) => sum + g.progress!, 0) /
              withProgress.length
          )
        : 0;

    const atRisk = (someChanges ?? 0) + (unlikely ?? 0);

    return { total, onTrack, atRisk, avgProgress };
  }, [enrichedGoals]);

  const getGoalList = () => {
    if (!enrichedGoals?.length) {
      return (
        <div className="flex justify-center items-center h-full px-5 py-10">
          <p className="text-[var(--text-secondary)]">
            No goals linked to this goal
          </p>
        </div>
      );
    }

    if (filteredGoals.length === 0) {
      return (
        <div className="flex justify-center items-center h-full px-5 py-10 text-center">
          <div>
            <p className="text-[13px] font-medium text-[var(--text-heading)] mb-1">
              No matching goals
            </p>
            <p className="text-xs text-[var(--text-secondary)]">
              No goals found matching "{search}"
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-y-auto no-scrollbar py-1">
        {filteredGoals?.map((goal) => (
          <button
            key={goal.goalId}
            className="px-3 pt-2 pb-3 border-b border-[var(--border-subtle)] hover:bg-[var(--sapphire-05)] transition-colors w-full text-left"
            onClick={() => {
              navigate(
                `/UserGuide/${goal.userAccountId}?tab=Goals&goalId=${goal.goalId}`
              );
              if (closeDrawerOnChildClick) {
                onClose();
              }
            }}
          >
            {/* Owner row */}
            <div className="flex items-center gap-2 mb-1.5">
              <AvatarCircle
                userAccountId={goal.userAccountId}
                size="small"
                className="scale-75"
              />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-[var(--text-heading)]">
                  {goal.user?.firstName} {goal.user?.lastName}
                </span>
                {goal.user?.jobTitle && (
                  <span className="text-[11px] text-[var(--text-label)] ml-1.5">
                    {goal.user.jobTitle}
                  </span>
                )}
              </div>
            </div>

            {/* Goal title */}
            <p className="text-[13px] font-medium text-[var(--text-body)] leading-[18px] mb-2 line-clamp-2">
              {goal.title}
            </p>

            {/* Status + Progress row */}

            <div className="flex items-center gap-3">
              {/* Status badge */}
              {goal.status ? (
                <ReflectionStatusBadge status={goal.status} />
              ) : (
                <div className="h-fit px-2 py-1 rounded-full w-fit flex align-items-center gap-sm border-[1px] border-[var(--border-subtle)] bg-[var(--slate-05)] font-medium">
                  <p className="text-[length:var(--fontSize-helper)]">
                    Not Started
                  </p>
                </div>
              )}

              {/* Progress bar using existing component */}
              <ReflectionProgressBar
                progressPercent={goal.progress ?? 0}
                colorOverride="var(--interactive)"
              />

              {/* Time remaining */}
              <span className="text-[11px] text-[var(--text-label)] whitespace-nowrap flex-shrink-0">
                {goal.timeLeft}
              </span>
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <Drawer direction="right" open={isOpen} onOpenChange={onClose}>
      <DrawerContent
        overlayClassName="bg-black/20"
        className={`right-0 top-0 left-auto bottom-auto mt-0 h-full ${GOAL_DRAWER_WIDTH} rounded-l-[10px] rounded-t-none`}
      >
        {/* ================= HEADER ================= */}
        <DrawerHeader className="d-flex justify-content-between align-items-center">
          <DrawerTitle>Linked Goals</DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary-blue">
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {/* ================= PARENT GOAL REFERENCE ================= */}
        <div className="px-3 py-2 bg-[var(--sapphire-05)] border-l-[3px] border-l-[var(--interactive)] mx-2 my-3 mt-0 rounded">
          <span className="text-[11px] text-[var(--text-label)] font-medium block mb-0.5">
            Goals linked to
          </span>
          <span className="text-[13px] font-medium text-[var(--text-heading)] line-clamp-2">
            {parentGoal?.title}
          </span>
        </div>

        {/* ================= STATS STRIP ================= */}
        <div className="grid grid-cols-4 gap-[1px] bg-[var(--border-subtle)]">
          <StatCell
            icon={<FontAwesomeIcon icon={faBullseye} />}
            label="Total"
            value={summary.total}
            color="var(--interactive)"
          />
          <StatCell
            icon={<FontAwesomeIcon icon={faArrowTrendUp} />}
            label="Avg. Progress"
            value={`${summary.avgProgress}%`}
            color="var(--interactive)"
          />
          <StatCell
            icon={<FontAwesomeIcon icon={faCircleCheck} />}
            label="On Track"
            value={summary.onTrack ?? 0}
            color={
              (summary.onTrack ?? 0) > 0
                ? "var(--status-success-text)"
                : "var(--text-secondary)"
            }
          />
          <StatCell
            icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
            label="At Risk"
            value={summary.atRisk}
            color={
              summary.atRisk > 0
                ? "var(--status-warning-text)"
                : "var(--text-secondary)"
            }
          />
        </div>

        <Separator />

        {/* ================= SEARCH ================= */}
        <div className="px-3 py-2">
          <DrawerSearchInput
            searchValue={search}
            setSearchValue={setSearch}
            placeholder="Search by goal, person, or team..."
          />
        </div>

        <Separator />

        {/* ================= LIST ================= */}
        {getGoalList()}

        {/* ================= FOOTER SUMMARY ================= */}
        <div className="px-5 py-2.5 border-t border-[var(--border-subtle)] text-xs text-[var(--text-label)] flex items-center justify-between">
          <span>
            {filteredGoals.length === summary.total
              ? `${summary.total} linked goal${getS(summary.total)}`
              : `${filteredGoals.length} of ${summary.total} shown`}
          </span>
          <span className="font-medium text-[var(--text-secondary)]">
            {summary.avgProgress}% avg. completion
          </span>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

/* ---------------- Stats Cell Component ---------------- */

function StatCell({
  icon,
  label,
  value,
  color,
}: Readonly<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
}>) {
  return (
    <div className="bg-[var(--surface-1)] px-3 py-2.5 flex flex-col items-center gap-0.5">
      <div style={{ color }}>{icon}</div>
      <span className="text-base font-semibold text-[var(--text-heading)] tracking-tight leading-5">
        {value}
      </span>
      <span className="text-[10px] font-medium text-[var(--text-label)] whitespace-nowrap">
        {label}
      </span>
    </div>
  );
}
